import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CertificatesEndpoints as Endpoints,
  type CreateCertificateRequest,
  type CreateCertificateResponse,
  type DeleteCertificateRequest,
  type DeleteCertificateResponse,
  type EditCertificateRequest,
  type EditCertificateResponse,
  type GetCertificateByIdRequest,
  type GetCertificateByIdResponse,
  type GetCertificatesForSaleRequest,
  type GetCertificatesForSaleResponse,
  type GetCertificatesRequest,
  type GetCertificatesResponse,
} from '@app/api/Certificates/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { CERTIFICATES_CHUNK } from '@app/constants';

const CERTIFICATES_TAG = 'Certificates';

export const certificatesApi = createApi({
  reducerPath: 'certificatesApi',
  baseQuery: createApiBaseQuery(CERTIFICATES_CHUNK),
  tagTypes: [CERTIFICATES_TAG],
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetCertificates]: query<
      GetCertificatesResponse,
      GetCertificatesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetCertificates,
        method: Methods.GET,
        params,
      }),
      providesTags: [CERTIFICATES_TAG],
    }),
    [Endpoints.GetCertificateById]: query<
      GetCertificateByIdResponse,
      GetCertificateByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetCertificateById,
        method: Methods.GET,
        params,
      }),
      providesTags: [CERTIFICATES_TAG],
    }),
    [Endpoints.CreateCertificate]: mutation<
      CreateCertificateResponse,
      CreateCertificateRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.CreateCertificate,
        method: Methods.POST,
        params,
        data,
      }),
      invalidatesTags: [CERTIFICATES_TAG],
    }),
    [Endpoints.EditCertificate]: mutation<
      EditCertificateResponse,
      EditCertificateRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.EditCertificate,
        method: Methods.PUT,
        params,
        data,
      }),
      invalidatesTags: [CERTIFICATES_TAG],
    }),
    [Endpoints.DeleteCertificate]: mutation<
      DeleteCertificateResponse,
      DeleteCertificateRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteCertificate,
        method: Methods.DELETE,
        params,
      }),
      invalidatesTags: [CERTIFICATES_TAG],
    }),
    [Endpoints.GetCertificatesForSale]: query<
      GetCertificatesForSaleResponse,
      GetCertificatesForSaleRequest
    >({
      query: (params) => ({
        url: Endpoints.GetCertificatesForSale,
        method: Methods.GET,
        params,
      }),
      providesTags: [CERTIFICATES_TAG],
    }),
  }),
});

export const {
  useGetCertificatesQuery,
  useLazyGetCertificatesQuery,
  useLazyGetCertificateByIdQuery,
  useCreateCertificateMutation,
  useEditCertificateMutation,
  useDeleteCertificateMutation,
  useLazyGetCertificatesForSaleQuery,
} = certificatesApi;
